<template>
  <header class="app-header">
    <div class="header-container">
      <div class="header-left">
        <!-- サイドバー開閉用ハンバーガーボタン -->
        <button
          type="button"
          class="btn-sidebar-toggle"
          aria-label="サイドバーの開閉"
          title="サイドバーの開閉"
          @click="uiStore.toggleSidebar"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>

        <!-- ロゴ -->
        <router-link :to="logoTarget" class="header-logo">
          <img src="/favicon.png" alt="" class="logo-icon logo-favicon" />
          <span class="logo-text">RabiiTrip</span>
        </router-link>
      </div>

      <!-- ユーザーメニュー -->
      <div class="header-actions">
        <div v-if="authStore.token" class="user-menu">
          <div class="user-avatar">
            <img
              v-if="authStore.user?.PROFILE_IMAGE_BASE64"
              :src="authStore.user.PROFILE_IMAGE_BASE64"
              alt="プロフィール画像"
              class="avatar-img"
              loading="lazy"
              decoding="async"
            />
            <IconSvg
              v-else
              name="user-solid-full"
              size="sm"
              class="avatar-placeholder"
            />
          </div>
          <span class="user-name">{{
            authStore.user?.USER_NAME || authStore.user?.email || "ユーザー"
          }}</span>
          <!-- 通知ベルアイコン -->
          <button
            v-if="!props.hideUtilityActions"
            @click="openNotifications"
            class="btn btn-icon notification-btn"
            title="通知"
          >
            <span class="bell-icon">
              <IconSvg name="bell-solid-full" size="sm" />
            </span>
            <span v-if="notificationCount > 0" class="notification-badge">{{
              notificationCount
            }}</span>
          </button>
          <button
            v-if="!props.hideUtilityActions"
            @click="openSettings"
            class="btn btn-icon"
            title="設定"
          >
            <IconSvg name="gear-solid-full" size="sm" />
          </button>
          <button @click="handleLogout" class="btn btn-secondary btn-sm">
            ログアウト
          </button>
        </div>
        <router-link v-else to="/login" class="btn btn-primary btn-sm">
          ログイン
        </router-link>
      </div>

      <button
        v-if="authStore.token && !props.hideUtilityActions"
        @click="openNotifications"
        class="btn btn-icon notification-btn mobile-header-notification"
        title="通知"
      >
        <span class="bell-icon">
          <IconSvg name="bell-solid-full" size="sm" />
        </span>
        <span v-if="notificationCount > 0" class="notification-badge">{{
          notificationCount
        }}</span>
      </button>

      <!-- モバイルメニューボタン -->
      <button class="mobile-menu-button" @click="toggleMobileMenu">
        <IconSvg name="ellipsis-solid-full" size="md" class="hamburger-icon" />
      </button>
    </div>

    <!-- モバイルメニュー -->
    <transition name="slide-down">
      <div v-if="isMobileMenuOpen" class="mobile-menu">
        <!-- モバイルユーザーメニュー -->
        <div v-if="authStore.token" class="mobile-user-section">
          <div class="mobile-user-info">
            <div class="user-avatar-mobile">
              <img
                v-if="authStore.user?.PROFILE_IMAGE_BASE64"
                :src="authStore.user.PROFILE_IMAGE_BASE64"
                alt="プロフィール画像"
                class="avatar-img"
                loading="lazy"
                decoding="async"
              />
              <IconSvg
                v-else
                name="user-solid-full"
                size="sm"
                class="avatar-placeholder"
              />
            </div>
            <span class="nav-text">{{
              authStore.user?.USER_NAME || authStore.user?.email || "ユーザー"
            }}</span>
          </div>
          <!-- 通知・設定ボタン（モバイル） -->
          <div class="mobile-action-row">
            <button
              v-if="!props.hideUtilityActions"
              @click="
                openNotifications();
                closeMobileMenu();
              "
              class="mobile-action-btn"
            >
              <span class="mobile-action-icon">
                <IconSvg name="bell-solid-full" size="sm" />
              </span>
              <span class="nav-text">通知</span>
              <span
                v-if="notificationCount > 0"
                class="mobile-notification-badge"
                >{{ notificationCount }}</span
              >
            </button>
            <button
              v-if="!props.hideUtilityActions"
              @click="
                openSettings();
                closeMobileMenu();
              "
              class="mobile-action-btn"
            >
              <span class="mobile-action-icon">
                <IconSvg name="gear-solid-full" size="sm" />
              </span>
              <span class="nav-text">設定</span>
            </button>
          </div>
          <button @click="handleLogout" class="mobile-nav-link logout-link">
            <span class="nav-icon">
              <IconSvg name="arrow-right-from-bracket-solid-full" size="sm" />
            </span>
            <span class="nav-text">ログアウト</span>
          </button>
        </div>
        <router-link
          v-else
          to="/login"
          class="mobile-nav-link"
          @click="closeMobileMenu"
        >
          <span class="nav-icon">
            <IconSvg name="user-solid-full" size="sm" />
          </span>
          <span class="nav-text">ログイン</span>
        </router-link>
      </div>
    </transition>
  </header>

  <!-- 設定モーダル -->
  <SettingsModal
    v-if="!props.hideUtilityActions"
    v-model="isSettingsOpen"
    :initial-view="settingsInitialView"
  />

  <!-- 通知モーダル -->
  <NotificationModal
    v-if="!props.hideUtilityActions"
    v-model="isNotificationOpen"
  />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/authStore.js";
import { useUserNotificationStore } from "@/store/user-notification-store";
import { useUiStore } from "@/store/ui-store";
import SettingsModal from "./SettingsModal.vue";
import NotificationModal from "./NotificationModal.vue";
import IconSvg from "./IconSvg.vue";

/* global defineProps */
const router = useRouter();
const authStore = useAuthStore();
const userNotifStore = useUserNotificationStore();
const uiStore = useUiStore();
const props = defineProps({
  hideMainNav: {
    type: Boolean,
    default: false,
  },
  hideUtilityActions: {
    type: Boolean,
    default: false,
  },
});
const isMobileMenuOpen = ref(false);
const isSettingsOpen = ref(false);
const settingsInitialView = ref("main");
const logoTarget = computed(() => (props.hideMainNav ? "/" : "/plan"));

const handleOpenSettingsEvent = (event) => {
  if (props.hideUtilityActions) return;
  settingsInitialView.value = event?.detail?.view || "main";
  isSettingsOpen.value = true;
};

onMounted(() => {
  window.addEventListener("open-settings", handleOpenSettingsEvent);
});

onUnmounted(() => {
  window.removeEventListener("open-settings", handleOpenSettingsEvent);
});
const isNotificationOpen = ref(false);

// 通知件数を計算
const notificationCount = computed(() => userNotifStore.totalCount);

// ヘッダーでは明細を取得せず、バッジ表示に必要な件数だけ取得する
onMounted(async () => {
  if (authStore.token && !props.hideUtilityActions) {
    await userNotifStore.fetchNotificationSummary();
  }
});

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

const openSettings = () => {
  if (props.hideUtilityActions) return;
  settingsInitialView.value = "main";
  isSettingsOpen.value = true;
};

const openNotifications = () => {
  if (props.hideUtilityActions) return;
  isNotificationOpen.value = true;
};

const handleLogout = () => {
  authStore.logout();
  closeMobileMenu();
  router.push("/");
};
</script>

<style scoped>
.app-header {
  background: var(--color-surface);
  box-shadow: 0 1px 0 var(--color-border);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.75rem 1rem; /* mobile */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem; /* mobile */
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-sidebar-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  padding: 0;
  border-radius: var(--radius-sm);
  transition: opacity var(--transition-fast), color var(--transition-fast);
}

.btn-sidebar-toggle:hover,
.btn-sidebar-toggle:active {
  color: var(--color-primary-ink);
  opacity: 0.8;
}

.btn-sidebar-toggle svg {
  width: 24px;
  height: 24px;
}

/* ロゴ */
.header-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 1.35rem;
  font-weight: 700;
  font-family: "M PLUS Rounded 1c", sans-serif;
  color: var(--color-text);
  transition: opacity var(--transition-fast);
}

.header-logo:hover {
  opacity: 0.8;
}

.logo-icon {
  font-size: 1.5rem;
}

.logo-favicon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.logo-text {
  font-size: 1.125rem; /* mobile */
}

/* ナビゲーション (モバイルでは非表示) */
.header-nav {
  display: none; /* mobile-first: hidden by default */
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  text-decoration: none;
  color: var(--color-text-muted);
  font-weight: 500;
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.nav-link:hover {
  background-color: var(--color-background);
  color: var(--color-text);
}

.nav-link.router-link-active {
  font-weight: 600;
  color: var(--color-text);
}

.nav-icon {
  font-size: 1.125rem;
}

.nav-text {
  font-size: 0.9375rem;
}

/* アクション (モバイルでは非表示) */
.header-actions {
  display: none; /* mobile-first: hidden by default */
  align-items: center;
  gap: 1rem;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-avatar-mobile {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  color: var(--text-tertiary);
}

.user-name {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  font-weight: 500;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-icon {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: background-color var(--transition-fast);
}

.btn-icon:hover {
  background-color: var(--color-background);
}

/* 通知ベルアイコン */
.notification-btn {
  position: relative;
}

.mobile-header-notification {
  display: inline-flex;
  position: relative;
  margin-left: auto;
}

.bell-icon {
  display: inline-block;
  animation: ring 2s ease-in-out infinite;
}

@keyframes ring {
  0%,
  100% {
    transform: rotate(0deg);
  }
  5%,
  15% {
    transform: rotate(-15deg);
  }
  10%,
  20% {
    transform: rotate(15deg);
  }
}

.notification-badge {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.125rem 0.375rem;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* モバイルメニューボタン (デフォルトで表示) */
.mobile-menu-button {
  display: block; /* mobile-first: visible by default */
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--color-text);
  transition: opacity var(--transition-fast);
}

.mobile-menu-button:hover {
  opacity: 0.7;
}

/* モバイルメニュー */
.mobile-menu {
  background: var(--color-surface);
  border-top: 1px solid var(--color-border-light);
  padding: 1rem;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  text-decoration: none;
  color: var(--color-text-muted);
  font-weight: 500;
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.mobile-nav-link:hover {
  background-color: var(--color-background);
  color: var(--color-text);
}

.mobile-nav-link.router-link-active {
  background-color: var(--color-primary-light);
  color: var(--primary-color);
}

.mobile-user-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  color: var(--color-text);
  font-weight: 600;
  font-size: 0.875rem;
}

.logout-link {
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.logout-link:hover {
  background-color: #fee2e2;
  color: #991b1b;
}

.mobile-menu-divider {
  height: 1px;
  background-color: var(--color-border-light);
  margin: 0.5rem 0;
}

/* アニメーション */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* レスポンシブ - モバイルファースト */
@media (min-width: 768px) {
  .header-container {
    padding: 1rem 2rem;
    gap: 2rem;
  }

  .header-nav {
    display: flex;
  }

  .header-actions {
    display: flex;
  }

  .mobile-header-notification {
    display: none;
  }

  .mobile-menu-button {
    display: none;
  }

  /* デスクトップではモバイルメニューを非表示 */
  .mobile-menu {
    display: none !important;
  }

  .logo-text {
    font-size: 1.25rem;
  }
}

/* モバイルアクション行 */
.mobile-action-row {
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
}

.mobile-action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: none;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-weight: 500;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  flex: 1;
  justify-content: center;
  position: relative;
  font-family: inherit;
}

.mobile-action-btn:hover {
  background-color: var(--color-background);
  color: var(--color-text);
}

.mobile-action-icon {
  font-size: 1.125rem;
}

.mobile-notification-badge {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.125rem 0.375rem;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

.mobile-menu-divider {
  height: 1px;
  background: var(--color-border-light);
  margin: 0.5rem 0;
}
</style>
